import {
  Card,
  CheckIcon,
  FilterIcon,
  FormControl,
  FormControlLabel,
  GKButton,
  GKDateRangePicker,
  GKModal,
  GKRadio,
  Grid2,
  Popover,
  RadioGroup,
  ReloadIcon,
  RightIcon,
  RupeeIcon,
  Show,
  Typography,
} from "components";
import dayjs from "dayjs";
import { useFetchQuery } from "hooks/useQueries";
import { map } from "lodash";
import { useState } from "react";
import { numberToCurrency } from "utils";
import { handleCapitalize } from "utils/common";
import { GKModalProps } from "utils/commonInterface";
import { matchDate } from "utils/helpers";
import GenerateTradeModal from "./GenerateTradeModal";
import TradeDetailsModal from "./TradeDetailsModal";

interface Props {
  basketId: number;
  basketDetails: {
    portfolioId: number;
    basketId: number;
    version: number;
    companyLogo: string;
  };
}

const InvestmentDetailsModal = (props: GKModalProps & Props) => {
  const { open, setOpen, basketId, basketDetails } = props;
  const [openTradeDetailsModal, setOpenTradeDetailsModal] = useState(false);
  const [openGenerateTradeModal, setOpenGenerateTradeModal] = useState(false);
  const [selectOrderId, setSelectOrderId] = useState(null);
  const [anchorEl, setAnchorEl] = useState<null | HTMLElement>(null);
  const [filters, setFilters] = useState({
    investment_type: "",
    start_date: "",
    end_date: "",
  });
  const [cleanFilter, setCleanFilter] = useState(false);

  const canOpen = Boolean(anchorEl);

  const { data, isFetching, refetch } = useFetchQuery({
    key: ["FETCH_CLIENT_BASKETS_INVESTMENT", cleanFilter],
    route: `/client/investments/`,
    requestBody: {
      basket_id: basketId,
      investment_type: filters.investment_type,
      start_date: filters.start_date,
      end_date: filters.end_date,
    },
  });

  const handleClick = (event: React.MouseEvent<HTMLElement>) => {
    setAnchorEl(event.currentTarget);
  };

  const handleApplyFilters = (newFilters: Partial<typeof filters>) => {
    setFilters((prev) => ({ ...prev, ...newFilters }));
    setAnchorEl(null);
  };

  return (
    <>
      <GKModal
        modalTitle={"My Investment Details"}
        open={open}
        setOpen={setOpen}
        width={"500px"}
        maxHeight="calc(100vh - 320px)"
        loading={isFetching}
        footer={
          <GKButton
            variant="contained"
            startIcon={<RupeeIcon />}
            onClick={() => setOpenGenerateTradeModal(true)}
          >
            Invest More
          </GKButton>
        }
      >
        <Grid2 container spacing={"12px"}>
          <Grid2
            size={12}
            display={"flex"}
            justifyContent={"space-between"}
            alignItems={"center"}
          >
            <GKButton
              variant="outlined"
              color="dark"
              startIcon={<FilterIcon />}
              onClick={handleClick}
            >
              Filter By
            </GKButton>
            <Typography
              fontSize={12}
              fontWeight={500}
              color="var(--neutral-500)"
              display={"flex"}
              alignItems={"center"}
              gap={0.5}
            >
              Total Entries:
              <Typography
                fontSize={12}
                fontWeight={600}
                color={"var(--neutral-500)"}
              >
                {data?.data?.data?.length || 0}
              </Typography>
            </Typography>
          </Grid2>
          <Show.When
            isTrue={data?.data?.data?.length !== 0}
            showNodata
            textContent="Not data found!"
          >
            <Grid2
              container
              size={12}
              maxHeight={"calc(100vh - 404px)"}
              overflow={"auto"}
              spacing={"12px"}
            >
              {map(data?.data?.data, (dataItem) => {
                return (
                  <Grid2 size={12} key={dataItem.orderId}>
                    <Card sx={{ p: 2 }}>
                      <Grid2 container size={12}>
                        <Grid2
                          size={12}
                          display={"flex"}
                          justifyContent={"space-between"}
                          alignItems={"center"}
                        >
                          <Typography fontSize={13} fontWeight={600}>
                            {matchDate(dataItem?.orderDate)}
                          </Typography>
                          <Typography fontSize={16} fontWeight={600}>
                            {numberToCurrency(dataItem?.executionTotalAmount)}
                          </Typography>
                        </Grid2>
                        <Grid2
                          size={12}
                          display={"flex"}
                          gap={0.5}
                          alignItems={"center"}
                        >
                          <Typography
                            fontSize={12}
                            fontWeight={500}
                            color="#697586"
                          >
                            Investment Type:
                          </Typography>
                          <Typography
                            fontSize={12}
                            fontWeight={500}
                            color="#222"
                          >
                            {handleCapitalize(dataItem?.orderType)}
                          </Typography>
                        </Grid2>
                        <Grid2 size={12} mt={2}>
                          <GKButton
                            variant="link"
                            sx={{
                              textDecoration: "none",
                              "&:hover": { textDecoration: "none" },
                            }}
                            endIcon={<RightIcon />}
                            onClick={() => {
                              setSelectOrderId(dataItem?.orderId);
                              setOpenTradeDetailsModal(true);
                            }}
                          >
                            View Trades
                          </GKButton>
                        </Grid2>
                      </Grid2>
                    </Card>
                  </Grid2>
                );
              })}
            </Grid2>
          </Show.When>
        </Grid2>
      </GKModal>

      <Show.When isTrue={openTradeDetailsModal}>
        <TradeDetailsModal
          open={openTradeDetailsModal}
          setOpen={setOpenTradeDetailsModal}
          selectOrderId={selectOrderId}
        />
      </Show.When>

      <Show.When isTrue={openGenerateTradeModal}>
        <GenerateTradeModal
          open={openGenerateTradeModal}
          setOpen={setOpenGenerateTradeModal}
          basketDetails={basketDetails}
        />
      </Show.When>

      <Popover
        open={canOpen}
        anchorEl={anchorEl}
        onClose={() => setAnchorEl(null)}
        slotProps={{
          paper: {
            sx: { width: "350px", borderRadius: "12px" },
          },
        }}
        anchorOrigin={{ vertical: "bottom", horizontal: "left" }}
        transformOrigin={{ vertical: "top", horizontal: "left" }}
      >
        <Grid2 container spacing={2} p={2}>
          <Grid2 size={12}>
            <Typography fontSize={12} fontWeight={600} color="#697586">
              INVESTMENT TYPE
            </Typography>
          </Grid2>
          <Grid2 size={12}>
            <FormControl>
              <RadioGroup
                defaultValue=""
                row
                value={filters.investment_type}
                onChange={(e) =>
                  setFilters((prev) => ({
                    ...prev,
                    investment_type: e.target.value,
                  }))
                }
              >
                <FormControlLabel
                  value=""
                  control={
                    <GKRadio
                      disableLeftPadding
                      disableTopPadding
                      disableBottomPadding
                    />
                  }
                  label="All"
                />
                <FormControlLabel
                  value="rebalance"
                  control={
                    <GKRadio
                      disableLeftPadding
                      disableTopPadding
                      disableBottomPadding
                    />
                  }
                  label="Rebalance"
                />
                <FormControlLabel
                  value="allocation"
                  control={
                    <GKRadio
                      disableLeftPadding
                      disableTopPadding
                      disableBottomPadding
                    />
                  }
                  label="Allocation"
                />
              </RadioGroup>
            </FormControl>
          </Grid2>
          <Grid2 size={12} mt={2}>
            <GKDateRangePicker
              inputLabel="Date Range"
              name="date"
              value={[dayjs(filters?.start_date), dayjs(filters?.end_date)]}
              onChange={(value: [dayjs.Dayjs | null, dayjs.Dayjs | null]) => {
                setFilters((prev) => ({
                  ...prev,
                  start_date: value[0] ? value[0].format("YYYY-MM-DD") : "",
                  end_date: value[1] ? value[1].format("YYYY-MM-DD") : "",
                }));
              }}
              slotProps={{ textField: { error: false, helperText: false } }}
            />
          </Grid2>
          <Grid2
            size={12}
            display={"flex"}
            alignItems={"center"}
            justifyContent={"space-between"}
            mt={2}
          >
            <GKButton
              variant="outlined"
              color="dark"
              startIcon={<ReloadIcon />}
              onClick={() => {
                setFilters({
                  investment_type: "",
                  start_date: "",
                  end_date: "",
                });
                setCleanFilter(true);
                setAnchorEl(null);
              }}
            >
              Reset All
            </GKButton>
            <GKButton
              variant="contained"
              startIcon={<CheckIcon />}
              onClick={() => {
                handleApplyFilters(filters);
                setCleanFilter(false);
                refetch();
              }}
            >
              Apply
            </GKButton>
          </Grid2>
        </Grid2>
      </Popover>
    </>
  );
};

export default InvestmentDetailsModal;
